import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from "@mui/material";
import MarkupTextEditor from "../../../components/utilities/MarkupTextEditor";
import {
  getAllPatchNotes,
  createPatchNote,
  updatePatchNote,
  deletePatchNote,
  setActivePatchNote
} from "../../../components/utilities/PatchNotesUtilities";
import { postNotification } from "../../../components/utilities/Notifications";
import { AuthContext } from "../../../components/user/BCPAuthProvider";

export default function AdminPatchNotesPanel() {
  const authContext = useContext(AuthContext);
  const userId = authContext?.authState?.identity?.userId;

  const [patches, setPatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "" });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const fetchPatches = async () => {
    try {
      const data = await getAllPatchNotes();
      const sortedData = [...data].sort(
        (a, b) => new Date(b.publishedDate) - new Date(a.publishedDate)
      );
      setPatches(sortedData);
    } catch (err) {
      console.error("Error fetching patch notes:", err);
      postNotification("error", "Failed to load patch notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatches();
  }, []);

  const handleOpenDialog = (patchId = null) => {
    if (patchId) {
      const patch = patches.find((p) => p.id === patchId);
      setFormData({ title: patch.title, content: patch.content });
      setEditingId(patchId);
    } else {
      setFormData({ title: "", content: "" });
      setEditingId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ title: "", content: "" });
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      postNotification("error", "Title and content are required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const currentPatch = patches.find((p) => p.id === editingId);
        await updatePatchNote(
          editingId,
          {
            title: formData.title,
            content: formData.content
          },
          userId,
          currentPatch
        );
        postNotification("success", "Patch note updated successfully");
      } else {
        await createPatchNote(formData.title, formData.content, userId);
        postNotification("success", "Patch note created successfully");
      }
      await fetchPatches();
      handleCloseDialog();
    } catch (err) {
      console.error("Error saving patch note:", err);
      postNotification("error", "Failed to save patch note");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (patchId) => {
    try {
      await deletePatchNote(patchId, userId);
      postNotification("success", "Patch note deleted successfully");
      await fetchPatches();
      setDeleteConfirmId(null);
    } catch (err) {
      console.error("Error deleting patch note:", err);
      postNotification("error", "Failed to delete patch note");
    }
  };

  const handleToggleActive = async (patchId, currentActive) => {
    try {
      await setActivePatchNote(patchId, !currentActive);
      postNotification(
        "success",
        currentActive ? "Patch note deactivated" : "Patch note activated"
      );
      await fetchPatches();
    } catch (err) {
      console.error("Error toggling patch note active state:", err);
      postNotification("error", "Failed to update patch note");
    }
  };

  const getDescriptionPreview = (content) => {
    if (!content) return "";
    var plainText = content.replace(/[#*`_~[\]()>-]/g, "").replace(/\n/g, " ");
    return plainText.length > 80
      ? `${plainText.substring(0, 80)}...`
      : plainText;
  };

  const handleTitleChange = (e) => {
    setFormData({ ...formData, title: e.target.value });
  };

  const handleContentChange = ({ text }) => {
    setFormData({ ...formData, content: text });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant="h5">Manage Patch Notes</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleOpenDialog()}
            >
              Create New Patch Note
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {patches.length === 0 ? (
            <Typography color="textSecondary">
              No patch notes created yet.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "banner.main",
                      color: "banner.contrastText",
                      "& th": {
                        color: "banner.contrastText",
                        fontWeight: "bold"
                      }
                    }}
                  >
                    <TableCell
                      sx={{ color: "banner.contrastText", width: "18%" }}
                    >
                      Title
                    </TableCell>
                    <TableCell
                      sx={{ color: "banner.contrastText", width: "32%" }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{ color: "banner.contrastText", width: "12%" }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ color: "banner.contrastText", width: "14%" }}
                    >
                      Active
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "banner.contrastText", width: "24%" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patches.map((patch) => (
                    <TableRow
                      key={patch.id}
                      sx={{ "&:hover": { backgroundColor: "action.hover" } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {patch.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 300
                          }}
                        >
                          {getDescriptionPreview(patch.content)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {new Date(patch.publishedDate).toLocaleDateString(
                            undefined,
                            { year: "numeric", month: "short", day: "numeric" }
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant={patch.active ? "contained" : "outlined"}
                          color={patch.active ? "success" : "primary"}
                          onClick={() =>
                            handleToggleActive(patch.id, patch.active)
                          }
                        >
                          {patch.active ? "Deactivate" : "Activate"}
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end"
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleOpenDialog(patch.id)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => setDeleteConfirmId(patch.id)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingId ? "Edit Patch Note" : "Create New Patch Note"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="e.g., Version 1.0.0 - Initial Release"
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Content (Markdown)
              </Typography>
              <MarkupTextEditor
                value={formData.content}
                onChange={handleContentChange}
                placeholder="Describe the changes and improvements..."
                sx={{ height: "400px" }}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Patch Note?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this patch note? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => handleDelete(deleteConfirmId)}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
